import Alert from "@/Components/Alert/Alert";
import Button from "@/Components/Button/Button";
import Icon from "@/Components/Icon/Icon";
import Spinner from "@/Components/Spinner/Spinner";
import useElectronHook from "@/shared/hooks/useElectronHook";
import useOrderInformationHook from "@/shared/hooks/useOrderInformationHook";
import { DeliveryMethods, OrderStatus } from "@/shared/utils/enum";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useLiveOrdersHook from "../../shared/hooks/useLiveOrdersHook";
import {
  getDeliveryBy,
  formatDate,
  getFullAddressV2,
  formatPrice,
} from "../../shared/utils/helpers";
import Modal, { ModalProps } from "../Modal/Modal";
import ZipcodeColor from "../ZipcodeColor";
import DenyUberOrder from "./DenyUberOrder";
import Order from "./Order";
import usePrintReceipt from "@/shared/hooks/usePrintReceipt";
import Tooltip from "../Tooltip/Tooltip";
import usePaymentHook from "@/shared/hooks/usePaymentHook";
import useSourceHook from "@/shared/hooks/useSourceHook";

export interface TimeModalProps extends ModalProps {
  isPending?: boolean;
  id?: string;
  name?: string;
  number?: string;
  price?: string;
  order: OrderV2;
  onApproveFinish?: () => void;
  isAutoAcceptOpen?: boolean;
  isOrderConfirmAsapMinButtonActive?: boolean;
}

interface MinutesProps {
  canHover?: boolean;
  className?: string;
  children?: React.ReactNode;
  onClick?: () => void;
}
const Minutes = ({ canHover, className, children, onClick }: MinutesProps) => {
  return (
    <div
      className={`cursor-pointer rounded-[0.5rem] bg-grey-blue px-10 py-[1.2rem] text-[2.5rem] font-bold text-white ${canHover ? "hover:bg-primary hover:opacity-75" : ""} ${className || ""}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

const AsapTimeChoices = ({ onClick, isPending }) => {
  const [range, setRange] = useState(0);
  const handleClick = (minute) => {
    if (isPending) return;
    onClick && onClick(minute);
  };

  return (
    <div className="mb-4 flex justify-evenly overflow-x-auto">
      <div
        onClick={() => setRange((prev) => prev - 1)}
        className="flex cursor-pointer items-center rounded-[5%] bg-light px-4 py-6"
        style={{
          pointerEvents: range === -1 ? "none" : "auto",
          visibility: range === -1 ? "hidden" : "visible",
        }}
      >
        <Icon
          className="h-[1.8rem] w-[1.8rem] fill-primary"
          variants="arrowLeft"
        />
      </div>
      {[15, 30, 45, 60, 90].slice(1 + range, 4 + range).map((minute) => (
        <Minutes onClick={() => handleClick(minute)} canHover>
          {isPending ? <Spinner color="white" /> : minute}
        </Minutes>
      ))}
      <div
        onClick={() => setRange((prev) => prev + 1)}
        className="flex cursor-pointer items-center rounded-[5%] bg-light px-4 py-6"
        style={{
          pointerEvents: range === 1 ? "none" : "auto",
          visibility: range === 1 ? "hidden" : "visible",
        }}
      >
        <Icon
          className="h-[1.8rem] w-[1.8rem] fill-primary"
          variants="arrowRight"
        />
      </div>
    </div>
  );
};

const TimeModal = ({
  order,
  onExit,
  onApproveFinish,
  isAutoAcceptOpen,
  isOrderConfirmAsapMinButtonActive,
  ...props
}: TimeModalProps) => {
  const { t } = useTranslation("");
  const [wantsDelete, setWantsDelete] = useState(false);
  const {
    hand_over_is_asap: isAsap,
    delivery_method_id: deliveryMethodId,
    hand_over_end_time,
    hand_over_time,
    order_status: orderStatus,
    distance_to_branch_in_km: distance,
    zipcode_color_code: colorCode,
    item_id: orderId,
    total_price: totalPrice,
    payment_method_id: paymentMethodId,
    source,
    isUber,
    remarks,
    canDeny,
    isWebsite,
    desired_hand_over_time,
    placed_date_time,
    branch_id,
  } = useOrderInformationHook(order);
  const { updateOrderTime, updateStatusToConfirmed } = useLiveOrdersHook();
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const { isElectron } = useElectronHook();
  const printReceipt = usePrintReceipt();
  const [orderTime, setOrderTime] = useState(
    dayjs(desired_hand_over_time ?? hand_over_end_time ?? hand_over_time)
  );
  const { getPayment } = usePaymentHook();
  const { getSource } = useSourceHook();

  useEffect(() => {
    if (orderTime.isBefore(dayjs(), "minutes")) {
      setOrderTime(dayjs().add(5, "minutes"));
    }
  }, []);

  const handleClickApprove = async (min?: number) => {
    if (isTimeInThePast && !isAsapBtnActive) {
      setShowAlert(true);
      return;
    }

    await updateOrderTime.mutateAsync({
      branchId: order.order_information.branch_id,
      orderId: orderId,
      time: isAsapBtnActive
        ? formatDate(dayjs().add(min, "minutes"), "time")
        : formatDate(orderTime, "time"),
      date: isAsapBtnActive
        ? formatDate(dayjs().add(min, "minutes"), "inputDate")
        : formatDate(orderTime, "inputDate"),
    });

    if (orderStatus !== OrderStatus.CONFIRMED) {
      await updateStatusToConfirmed.mutateAsync({
        orderId,
        branchId: branch_id,
      });
      if (isElectron) {
        printReceipt(orderId, branch_id);
      }
    }
    setWantsDelete(false);
    onApproveFinish && onApproveFinish();
  };

  const denyClick = () => {
    setWantsDelete(true);
  };

  const handleSetOrderTime = (minutes: number) => {
    const newDate = orderTime.add(minutes, "minutes");
    setOrderTime(newDate);
  };

  const minuteDiff = dayjs(orderTime).diff(dayjs(placed_date_time), "minute");
  const address = useMemo(() => order && getFullAddressV2(order), [order]);
  const isTimeInThePast = orderTime <= dayjs();
  const isAsapBtnActive = isAsap && isOrderConfirmAsapMinButtonActive;

  return (
    <Modal
      width={"50rem"}
      onExit={() => {
        onExit();
        setWantsDelete(false);
      }}
      {...props}
    >
      <div className="flex gap-2" style={{ fontWeight: 600 }}>
        {`${t("timeChoice")}:`}
        <div className="font-bold">
          {isAsap
            ? t("asap")
            : formatDate(
                desired_hand_over_time ?? hand_over_end_time,
                "dateTime"
              )}
        </div>
      </div>
      <hr className="border border-transparent bg-grey" />
      <div>{`${t("totalPrice")}:  ${formatPrice(totalPrice)}`}</div>

      {!isUber && (
        <div className="flex items-center gap-4">
          {address &&
            `${t("address")}: ${address} ${distance ? `${distance}km` : ""}`}
          {colorCode && <ZipcodeColor color={colorCode} />}
        </div>
      )}

      {!isUber && (
        <div>
          {`${t("paymentMethod")}: ${
            order && t(`${getPayment(paymentMethodId)?.translationKey}`)
          } `}
        </div>
      )}
      <div>{`${t("source")}: ${t(getSource(source)?.translationKey)} `}</div>
      {order?.order_user?.first_name && (
        <div>
          {`${t("name")}: ${order?.order_user?.first_name} ${
            order?.order_user?.last_name
          }`}
        </div>
      )}
      {order?.order_user?.telephone && (
        <div>{`${t("telephone")}: ${order?.order_user?.telephone}`}</div>
      )}

      {deliveryMethodId !== DeliveryMethods.POS_ORDER && (
        <div>
          {`${t("deliveryMethod")}: ${t(
            `${getDeliveryBy(deliveryMethodId)}`
          )} `}
        </div>
      )}

      {remarks && (
        <div style={{ fontWeight: "900" }}>
          {t("remarks", { value: remarks })}
        </div>
      )}

      <div className="mt-[1rem]">{order && <Order orders={order} />}</div>
      <hr className="border border-transparent bg-grey" />
      {showAlert && (
        <Alert onClickClose={() => setShowAlert(false)}>
          {t("orders_live.pastTimeWarning")}
        </Alert>
      )}
      {isUber && (
        <div style={{ fontWeight: "900" }}>{t("uberWarningTime")}</div>
      )}
      <div className="mt-8 flex flex-col justify-between">
        {isAsap && isOrderConfirmAsapMinButtonActive ? (
          <AsapTimeChoices
            isPending={
              updateOrderTime.isPending || updateStatusToConfirmed.isPending
            }
            onClick={(min) => handleClickApprove(min)}
          />
        ) : (
          <div className="mb-2.5 flex justify-center">
            {!isTimeInThePast && (
              <Minutes onClick={() => handleSetOrderTime(-5)}>-</Minutes>
            )}
            <Minutes className="mx-[2rem]">
              {formatDate(orderTime, "time")}
            </Minutes>
            {(!isUber || minuteDiff < 60) && (
              <Minutes onClick={() => handleSetOrderTime(5)}>+</Minutes>
            )}
          </div>
        )}
      </div>
      {isUber && minuteDiff >= 60 && (
        <div className="text-smaller mb-[1rem] text-error">
          {t("uberOrderCannotBeMovedOneHourLater")}
        </div>
      )}

      <div className="flex w-full gap-4">
        {!isAsapBtnActive && (
          <div className="w-full">
            <Button
              className="w-full"
              type="button"
              isPending={
                updateOrderTime.isPending || updateStatusToConfirmed.isPending
              }
              onClick={() => handleClickApprove()}
            >
              {isAutoAcceptOpen ? t("edit") : t("approve")}
            </Button>
          </div>
        )}
        {canDeny && (
          <div className="w-full">
            {isWebsite ? (
              <Tooltip content={t("refundWarning")}>
                <div>
                  <Button
                    className="w-full"
                    onClick={denyClick}
                    type="button"
                    variant="danger"
                  >
                    {t("deny")}
                  </Button>
                </div>
              </Tooltip>
            ) : (
              <Button
                className="w-full"
                onClick={denyClick}
                type="button"
                variant="danger"
              >
                {t("deny")}
              </Button>
            )}
          </div>
        )}
      </div>
      {wantsDelete && <DenyUberOrder order={order} />}
    </Modal>
  );
};

export default TimeModal;
